const REFUSED_ID_STORAGE_KEY = 'palen.analytics.anonymous_id';
const CONSENTED_ID_STORAGE_KEY = 'palen.analytics.consented_id';
const LOCAL_JOURNEY_ID_PATTERN = /^anon_[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}(?:\.[A-Za-z0-9_-]{43})?$/i;

type JourneyIdentityMode = 'consented' | 'refused';

export class AnalyticsIdentityService {
  private readonly identities: Partial<Record<JourneyIdentityMode, string>> = {};

  anonymousId(persist = true): string {
    return this.refusedId(persist);
  }

  refusedId(persist = true): string {
    return this.identity('refused', persist);
  }

  consentedId(persist = true): string {
    return this.identity('consented', persist);
  }

  replaceAnonymousId(distinctId: string): void {
    this.replaceIdentity('refused', distinctId);
  }

  replaceConsentedId(distinctId: string): void {
    this.replaceIdentity('consented', distinctId);
  }

  rotateAnonymousId(): string {
    return this.rotateIdentity('refused');
  }

  rotateConsentedId(): string {
    return this.rotateIdentity('consented');
  }

  private identity(mode: JourneyIdentityMode, persist: boolean): string {
    if (this.identities[mode]) return this.identities[mode];
    const identity = (persist ? this.readStoredIdentity(mode) : null) || this.createIdentity();
    this.identities[mode] = identity;
    if (persist) this.storeIdentity(mode, identity);
    return identity;
  }

  private replaceIdentity(mode: JourneyIdentityMode, distinctId: string): void {
    const normalizedId = distinctId.trim();
    if (!normalizedId) return;
    this.identities[mode] = normalizedId;
    this.storeIdentity(mode, normalizedId);
  }

  private rotateIdentity(mode: JourneyIdentityMode): string {
    const identity = this.createIdentity();
    this.replaceIdentity(mode, identity);
    return identity;
  }

  private createIdentity(): string {
    return `anon_${crypto.randomUUID()}`;
  }

  private readStoredIdentity(mode: JourneyIdentityMode): string | null {
    try {
	  const stored = window.localStorage.getItem(this.storageKey(mode));
	  if (mode === 'refused' && stored && !LOCAL_JOURNEY_ID_PATTERN.test(stored)) {
		window.localStorage.removeItem(this.storageKey(mode));
		return null;
	  }
	  return stored;
    } catch {
      return null;
    }
  }

  private storeIdentity(mode: JourneyIdentityMode, distinctId: string): void {
    try {
      window.localStorage.setItem(this.storageKey(mode), distinctId);
    } catch {
      // The in-memory identifier still separates visitors for this page lifetime.
    }
  }

  private storageKey(mode: JourneyIdentityMode): string {
    return mode === 'refused' ? REFUSED_ID_STORAGE_KEY : CONSENTED_ID_STORAGE_KEY;
  }
}

export const analyticsIdentityService = new AnalyticsIdentityService();
